<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import { useChannelIcon } from 'dashboard/components-next/icon/provider';
import { getInboxSource } from 'dashboard/helper/inbox';

export default {
  name: 'FilterInput',
  components: {
    NextButton,
    ChannelIcon,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => {},
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
    inputType: {
      type: String,
      default: 'plain_text',
    },
    operators: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    // Shows the AND connector without letting it change (delayed automation rules are AND-only).
    lockQueryOperator: {
      type: Boolean,
      default: false,
    },
    showQueryOperator: {
      type: Boolean,
      default: false,
    },
    showUserInput: {
      type: Boolean,
      default: true,
    },
    groupedFilters: {
      type: Boolean,
      default: false,
    },
    filterGroups: {
      type: Array,
      default: () => [],
    },
    customAttributeType: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'removeFilter', 'resetFilter'],
  computed: {
    attributeKey: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.attribute_key;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, attribute_key: value });
      },
    },
    filterOperator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.filter_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, filter_operator: value });
      },
    },
    values: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.values;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, values: value });
      },
    },
    query_operator: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.query_operator;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, query_operator: value });
      },
    },
    custom_attribute_type: {
      get() {
        if (!this.customAttributeType) return '';
        return this.customAttributeType;
      },
      set() {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', {
          ...payload,
          custom_attribute_type: this.customAttributeType,
        });
      },
    },
    noticeKey: {
      get() {
        return `AUTOMATION.NOTICES.${this.attributeKey?.toUpperCase()}`;
      },
    },
    // The inbox condition gets the values of the inbox list: every inbox grouped
    // with the ones carrying the same channel icon, alphabetical inside a group.
    // Every other condition (agents, teams, labels) keeps the order it is given.
    sortedDropdownValues() {
      if (!this.dropdownValues.some(option => option.channel_type)) {
        return this.dropdownValues;
      }

      return [...this.dropdownValues].sort((a, b) => {
        const iconA = this.inboxIcon(a);
        const iconB = this.inboxIcon(b);
        // Plain comparison: an icon name is not prose, and collation rules for
        // its digits and hyphens would scatter the groups.
        if (iconA !== iconB) return iconA < iconB ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '');
      });
    },
  },
  watch: {
    customAttributeType: {
      handler(value) {
        if (
          value === 'conversation_attribute' ||
          value === 'contact_attribute'
        ) {
          // eslint-disable-next-line vue/no-mutating-props
          this.modelValue.custom_attribute_type = this.customAttributeType;
          // eslint-disable-next-line vue/no-mutating-props
        } else this.modelValue.custom_attribute_type = '';
      },
      immediate: true,
    },
  },
  methods: {
    inboxIcon(option) {
      return option.channel_type ? useChannelIcon(option).value : '';
    },
    // The address behind the inbox — mail address, number or site — the same one
    // the inbox list shows next to the channel.
    inboxSource(option) {
      if (!option.channel_type) return '';
      return getInboxSource(option.channel_type, option.phone_number, option);
    },
    removeFilter() {
      this.$emit('removeFilter');
    },
    resetFilter() {
      this.$emit('resetFilter');
    },
    getInputErrorClass(errorMessage) {
      return errorMessage
        ? 'bg-n-ruby-8/20 border-n-ruby-5 dark:border-n-ruby-5'
        : 'bg-n-background border-n-weak dark:border-n-weak';
    },
  },
};
</script>

<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div>
    <div
      class="p-2 border border-solid rounded-lg"
      :class="getInputErrorClass(errorMessage)"
    >
      <div class="flex gap-1">
        <select
          v-if="groupedFilters"
          v-model="attributeKey"
          class="max-w-[30%] mb-0 mr-1"
          @change="resetFilter()"
        >
          <optgroup
            v-for="(group, i) in filterGroups"
            :key="i"
            :label="group.name"
          >
            <option
              v-for="attribute in group.attributes"
              :key="attribute.key"
              :value="attribute.key"
              :selected="true"
            >
              {{ attribute.name }}
            </option>
          </optgroup>
        </select>
        <select
          v-else
          v-model="attributeKey"
          class="max-w-[30%] mb-0 mr-1"
          @change="resetFilter()"
        >
          <option
            v-for="attribute in filterAttributes"
            :key="attribute.key"
            :value="attribute.key"
            :disabled="attribute.disabled"
          >
            {{ attribute.name }}
          </option>
        </select>

        <select v-model="filterOperator" class="max-w-[20%] mb-0 mr-1">
          <option
            v-for="(operator, o) in operators"
            :key="o"
            :value="operator.value"
          >
            {{ $t(`FILTER.OPERATOR_LABELS.${operator.value}`) }}
          </option>
        </select>

        <div v-if="showUserInput" class="flex-grow mr-1 filter__answer--wrap">
          <div
            v-if="inputType === 'multi_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="sortedDropdownValues"
              :allow-empty="false"
            >
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
              <template #noResult>
                {{ $t('FORMS.MULTISELECT.NO_RESULT') }}
              </template>
              <template #option="{ option }">
                <div class="flex items-center gap-1 min-w-0">
                  <ChannelIcon
                    v-if="option.channel_type"
                    :inbox="option"
                    class="flex-shrink-0 !w-4 !h-4 text-n-slate-11"
                  />
                  <span class="truncate">{{ option.name }}</span>
                  <template v-if="inboxSource(option)">
                    <span aria-hidden="true">·</span>
                    <span class="truncate text-n-slate-11">
                      {{ inboxSource(option) }}
                    </span>
                  </template>
                </div>
              </template>
              <template #tag="{ option, remove }">
                <span class="multiselect__tag">
                  <ChannelIcon
                    v-if="option.channel_type"
                    :inbox="option"
                    class="flex-shrink-0 !w-3.5 !h-3.5 -mb-0.5 mr-1 inline-block"
                  />
                  <span>{{ option.name }}</span>
                  <i
                    tabindex="1"
                    class="multiselect__tag-icon"
                    @keypress.enter.prevent="remove(option)"
                    @mousedown.prevent="remove(option)"
                  />
                </span>
              </template>
            </multiselect>
          </div>
          <div
            v-else-if="inputType === 'search_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="values"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="sortedDropdownValues"
              :allow-empty="false"
              :option-height="104"
            >
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
              <template #noResult>
                {{ $t('FORMS.MULTISELECT.NO_RESULT') }}
              </template>
              <template #option="{ option }">
                <div class="flex items-center gap-1 min-w-0">
                  <ChannelIcon
                    v-if="option.channel_type"
                    :inbox="option"
                    class="flex-shrink-0 !w-4 !h-4 text-n-slate-11"
                  />
                  <span class="truncate">{{ option.name }}</span>
                  <template v-if="inboxSource(option)">
                    <span aria-hidden="true">·</span>
                    <span class="truncate text-n-slate-11">
                      {{ inboxSource(option) }}
                    </span>
                  </template>
                </div>
              </template>
            </multiselect>
          </div>
          <div v-else-if="inputType === 'date'" class="multiselect-wrap--small">
            <input
              v-model="values"
              type="date"
              :editable="false"
              class="!mb-0 datepicker"
            />
          </div>
          <input
            v-else
            v-model="values"
            type="text"
            class="!mb-0"
            :placeholder="$t('FILTER.INPUT_PLACEHOLDER')"
          />
        </div>
        <NextButton
          icon="i-lucide-x"
          slate
          ghost
          class="flex-shrink-0"
          @click="removeFilter"
        />
      </div>
      <p v-if="errorMessage" class="filter-error">
        {{ errorMessage }}
      </p>
      <p v-if="$te(noticeKey)" class="filter-notice">
        {{ $t(noticeKey) }}
      </p>
    </div>

    <div
      v-if="showQueryOperator"
      class="flex items-center justify-center relative my-2.5 mx-0"
    >
      <hr
        class="absolute w-full border-b border-solid border-slate-75 dark:border-slate-800"
      />
      <span
        v-if="lockQueryOperator"
        class="relative px-3 py-1 text-sm rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-solid border-slate-75 dark:border-slate-600"
      >
        {{ $t('FILTER.QUERY_DROPDOWN_LABELS.AND') }}
      </span>
      <select
        v-else
        v-model="query_operator"
        class="relative w-auto mb-0 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border-slate-75 dark:border-slate-600"
      >
        <option value="and">
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.AND') }}
        </option>
        <option value="or">
          {{ $t('FILTER.QUERY_DROPDOWN_LABELS.OR') }}
        </option>
      </select>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter__answer--wrap {
  input {
    @apply bg-white dark:bg-slate-900 mb-0 text-slate-800 dark:text-slate-100 border-slate-75 dark:border-slate-600;
  }
}

.filter-error {
  @apply text-n-ruby-9 dark:text-n-ruby-9 block my-1 mx-0;
}

.filter-notice {
  @apply text-amber-500 dark:text-amber-500 block my-1 mx-0;
}

.multiselect {
  @apply mb-0;
}
</style>
